/**
 * The ad's socket (docs/SPEC.md §25.4, `WS /v1/ads/{id}/events`) as a hook:
 * every event is written into the ad's query, so the page renders from one
 * object whether it came from `GET /v1/ads/{id}` or from here.
 *
 * Events: `hello` (the whole ad), `state` (the ad's state with its error and
 * sentence), `brief` (the conversation), `version` (a version's line; the
 * tree and the ad's manifests are read again). P22-T08: `change` goes into
 * the ad's changes (`api/changes.ts`), `progress` into `progressKey(id)` (the
 * box's "Rebuilding…" fraction; P28-T09: a progress naming a scene now built
 * reads the ad's manifests again, so the scene joins the strip), and
 * every event is also handed to `listen` (the film state gives an instant
 * change's `patches` to the player). P24-T05: `output`, `frame` and `still`
 * go into the ways out's queries (`api/outputs.applyOutputEvent`). P28-T14:
 * `cast` (the cast panel's rows, §6.3) goes into `castKey(id)`.
 */
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import {
  type AdDetail,
  type AdState,
  type AdVersion,
  type BriefState,
  adKey,
} from "../api/ads";
import { type CastRow, castKey } from "../api/cast";
import { type Change, writeChange } from "../api/changes";
import { ME_KEY } from "../api/me";
import { applyOutputEvent } from "../api/outputs";
import { manifestKey, versionsKey } from "../api/versions";
import {
  type SocketEvent,
  type SocketStatus,
  useSocket,
} from "../ui/useSocket";

/** The ad with one event applied; `hello` replaces it. Pure, for tests. */
export function applyAdEvent(
  ad: AdDetail | undefined,
  event: SocketEvent,
): AdDetail | undefined {
  if (event.type === "hello") return event.ad as AdDetail;
  if (!ad) return ad;
  switch (event.type) {
    case "state":
      return {
        ...ad,
        state: event.state as AdState,
        error_code: (event.error_code as string | null) ?? null,
        sentence: (event.sentence as string | null) ?? null,
      };
    case "brief": {
      const brief_state = event.brief as BriefState;
      return {
        ...ad,
        brief_state,
        brief: brief_state.final ? brief_state.brief : ad.brief,
        title: brief_state.title ?? ad.title,
      };
    }
    case "version": {
      const version = event.version as AdVersion;
      const building =
        version.state === "building"
          ? version
          : ad.building_version?.id === version.id
            ? null
            : ad.building_version;
      return {
        ...ad,
        current_version: version.is_current ? version : ad.current_version,
        building_version: building,
      };
    }
    default:
      return ad;
  }
}

/** A `progress` event (§25.4): scenes of version `version` baked; `scene`
 *  the one just built and now playable (`ready`). */
export interface AdProgress {
  stage: "bake";
  done: number;
  total: number;
  fraction: number;
  version: number | null;
  scene: string | null;
  ready: boolean;
}

export const progressKey = (id: string) => ["ad-progress", id] as const;

/** A change's ends: its hold is settled or released (§15.1). */
const CHANGE_ENDS = ["applied", "cannot", "declined", "failed", "cancelled"];

/** Whether the event follows a hold, a settle or a release (§15.1): the
 *  balance chip is read again after each (P28-T08). */
export function movesCredits(event: SocketEvent): boolean {
  switch (event.type) {
    case "change": {
      const state = (event.change as { state?: string } | undefined)?.state;
      return !!state && CHANGE_ENDS.includes(state);
    }
    case "brief":
      // a brief turn ended: settled, or released when it failed
      return !(event.brief as { working?: boolean } | undefined)?.working;
    case "state":
    case "still":
      return true;
    case "version": {
      const state = (event.version as { state?: string } | undefined)?.state;
      return state === "ready" || state === "failed";
    }
    default:
      return false;
  }
}

function write(queryClient: QueryClient, id: string, event: SocketEvent) {
  queryClient.setQueryData<AdDetail>(adKey(id), (ad) =>
    applyAdEvent(ad, event),
  );
  if (movesCredits(event)) {
    void queryClient.invalidateQueries({ queryKey: ME_KEY });
  }
  if (event.type === "change" && event.change) {
    writeChange(queryClient, id, event.change as Change);
  } else if (event.type === "version") {
    void queryClient.invalidateQueries({ queryKey: versionsKey(id) });
    readManifests(queryClient, id);
  } else if (event.type === "cast" && Array.isArray(event.rows)) {
    queryClient.setQueryData(castKey(id), { rows: event.rows as CastRow[] });
  } else if (
    event.type === "output" ||
    event.type === "frame" ||
    event.type === "still"
  ) {
    applyOutputEvent(queryClient, id, event);
  } else if (event.type === "progress") {
    const { stage, done, total, fraction, version, scene, ready } =
      event as unknown as AdProgress;
    queryClient.setQueryData<AdProgress>(progressKey(id), {
      stage,
      done,
      total,
      fraction,
      version,
      scene,
      ready: !!ready,
    });
    if (ready) readManifests(queryClient, id);
  }
}

/** The ad's manifests read again: a version's scenes built so far. */
function readManifests(queryClient: QueryClient, id: string) {
  const prefix = `/v1/ads/${encodeURIComponent(id)}/`;
  void queryClient.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === manifestKey("")[0] &&
      String(q.queryKey[1]).startsWith(prefix),
  });
}

export function useAdSocket(
  id: string,
  enabled = true,
  listen?: (event: SocketEvent) => void,
): SocketStatus {
  const queryClient = useQueryClient();
  const listenRef = useRef(listen);
  listenRef.current = listen;
  const onEvent = useCallback(
    (event: SocketEvent) => {
      write(queryClient, id, event);
      listenRef.current?.(event);
    },
    [queryClient, id],
  );
  return useSocket(`/v1/ads/${encodeURIComponent(id)}/events`, onEvent, {
    enabled,
  });
}
